"use client";

import React from "react";
import { Loader, ChevronDown } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
export default function TeamRegistration() {
  return <section id="inscription-de-léquipe" className="bg-secondary py-24 px-4 sm:px-6 lg:px-8 flex justify-center items-center min-h-screen relative overflow-hidden">
      {}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-primary/5 rounded-full blur-3xl pointer-events-none"></div>

      <div className="w-full max-w-3xl border border-primary/50 p-8 sm:p-12 rounded-xl bg-background/10 backdrop-blur-md shadow-2xl relative z-10">
        <div className="text-center mb-12">
          <h2 className="text-3xl sm:text-4xl font-bold text-secondary-foreground font-default uppercase tracking-tight mb-4">
            Inscription de l'équipe
          </h2>
          <p className="text-secondary-foreground/70 font-sans max-w-xl mx-auto">
            Veuillez remplir le formulaire ci-dessous avec les informations de votre club et de votre équipe pour valider votre participation au tournoi.
          </p>
        </div>

        <FormProvider formId="team_registration_form_tournamentdetail" sectionName="Team Registration" defaultValues={{
        club_name: "",
        team_name: "",
        coach_name: "",
        email: "",
        phone: "",
        category: "",
        notes: ""
      }} validationRules={{
        club_name: {
          required: "Le nom du club est requis"
        },
        team_name: {
          required: "Le nom de l'équipe est requis"
        },
        coach_name: {
          required: "Le nom du coach est requis"
        },
        email: {
          required: "L'adresse email est requise",
          pattern: {
            value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
            message: "Veuillez entrer une adresse email valide"
          }
        },
        phone: {
          required: "Le numéro de téléphone est requis",
          pattern: {
            value: /^[+]*[(]{0,1}[0-9]{1,4}[)]{0,1}[-\s\./0-9]*$/,
            message: "Veuillez entrer un numéro de téléphone valide"
          }
        },
        category: {
          required: "Veuillez sélectionner une catégorie"
        }
      }} submitText="Soumettre l'inscription" successMessage="Votre inscription a été soumise avec succès. Notre équipe vous contactera très prochainement pour la suite des démarches.">
          <RegistrationFormInner />
        </FormProvider>
      </div>
    </section>;
}
function RegistrationFormInner() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="p-10 bg-primary/10 border border-primary/30 rounded-lg text-center animate-in fade-in zoom-in duration-500">
        <div className="w-16 h-16 bg-primary/20 rounded-full flex items-center justify-center mx-auto mb-6">
          <svg className="w-8 h-8 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <h3 className="text-2xl font-bold text-primary mb-4 font-default uppercase tracking-wide">Inscription Réussie !</h3>
        <p className="text-secondary-foreground/90 mb-8 text-lg">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="border-primary text-primary hover:bg-primary hover:text-primary-foreground uppercase tracking-wider font-bold h-12 px-8">
          Inscrire une autre équipe
        </Button>
      </div>;
  }
  const inputClasses = "bg-secondary/40 border-0 border-b border-border/50 rounded-none px-2 py-2 h-12 text-secondary-foreground placeholder:text-secondary-foreground/30 focus-visible:border-primary focus-visible:ring-0 focus-visible:ring-offset-0 shadow-none transition-colors duration-300";
  const labelClasses = "text-[12px] uppercase font-medium tracking-widest text-secondary-foreground/70 font-sans mb-1 block";
  return <Form onSubmit={handleSubmit} className="space-y-8">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-8">
        <FormField control={control} name="club_name" render={({
        field
      }) => <FormItem>
              <FormLabel className={labelClasses}>Nom du club *</FormLabel>
              <FormControl>
                <Input placeholder="Club ABC" className={inputClasses} {...field} />
              </FormControl>
              <FormDescription className="text-[11px] text-secondary-foreground/40 mt-1">Indiquez le nom complet du club auquel appartient l'équipe.</FormDescription>
              <FormMessage className="text-xs mt-1" />
            </FormItem>} />

        <FormField control={control} name="team_name" render={({
        field
      }) => <FormItem>
              <FormLabel className={labelClasses}>Nom de l'équipe *</FormLabel>
              <FormControl>
                <Input placeholder="Les Tigres" className={inputClasses} {...field} />
              </FormControl>
              <FormDescription className="text-[11px] text-secondary-foreground/40 mt-1">Nom sous lequel votre équipe sera inscrite au tournoi.</FormDescription>
              <FormMessage className="text-xs mt-1" />
            </FormItem>} />

        <FormField control={control} name="coach_name" render={({
        field
      }) => <FormItem>
              <FormLabel className={labelClasses}>Nom du coach *</FormLabel>
              <FormControl>
                <Input placeholder="Jean Dupont" className={inputClasses} {...field} />
              </FormControl>
              <FormMessage className="text-xs mt-1" />
            </FormItem>} />

        <FormField control={control} name="category" render={({
        field
      }) => <FormItem>
              <FormLabel className={labelClasses}>Catégorie / Âge *</FormLabel>
              <FormControl>
                <div className="relative">
                  <select className={`w-full flex ${inputClasses} appearance-none bg-transparent cursor-pointer`} {...field}>
                    <option value="" disabled className="text-secondary-foreground/40 bg-secondary">Sélectionner la catégorie</option>
                    <option value="U12" className="bg-secondary text-secondary-foreground">U12</option>
                    <option value="U14" className="bg-secondary text-secondary-foreground">U14</option>
                    <option value="U16" className="bg-secondary text-secondary-foreground">U16</option>
                    <option value="U18" className="bg-secondary text-secondary-foreground">U18</option>
                    <option value="Seniors" className="bg-secondary text-secondary-foreground">Seniors</option>
                  </select>
                  <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 size-4 text-secondary-foreground/50 pointer-events-none" />
                </div>
              </FormControl>
              <FormMessage className="text-xs mt-1" />
            </FormItem>} />

        <FormField control={control} name="email" render={({
        field
      }) => <FormItem>
              <FormLabel className={labelClasses}>Adresse email *</FormLabel>
              <FormControl>
                <Input type="email" placeholder="coach@example.com" className={inputClasses} {...field} />
              </FormControl>
              <FormMessage className="text-xs mt-1" />
            </FormItem>} />

        <FormField control={control} name="phone" render={({
        field
      }) => <FormItem>
              <FormLabel className={labelClasses}>Numéro de téléphone *</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="+33 6 12 34 56 78" className={inputClasses} {...field} />
              </FormControl>
              <FormDescription className="text-[11px] text-secondary-foreground/40 mt-1">Numéro de contact du responsable (format international recommandé).</FormDescription>
              <FormMessage className="text-xs mt-1" />
            </FormItem>} />
      </div>

      <FormField control={control} name="notes" render={({
      field
    }) => <FormItem className="pt-4">
            <FormLabel className={labelClasses}>Commentaires / Infos complémentaires</FormLabel>
            <FormControl>
              <textarea placeholder="Ex : besoins spécifiques, nombre de joueurs, etc." className={`w-full min-h-[120px] resize-y ${inputClasses} py-3`} {...field} />
            </FormControl>
            <FormMessage className="text-xs mt-1" />
          </FormItem>} />

      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-lg flex items-start gap-3">
          <svg className="w-5 h-5 text-destructive shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <p className="text-destructive text-sm font-medium">{submitError}</p>
        </div>}

      <div className="pt-6">
        <Button type="submit" disabled={isSubmitting} className="w-full h-16 text-lg font-bold uppercase tracking-widest bg-gradient-to-r from-primary to-chart-1 text-primary-foreground hover:opacity-90 transition-all duration-300 border-0 rounded-md shadow-[0_0_20px_rgba(var(--color-primary),0.3)] hover:shadow-[0_0_30px_rgba(var(--color-primary),0.5)]">
          {isSubmitting ? <>
              <Loader className="animate-spin mr-3 size-5" />
              Traitement en cours...
            </> : "Soumettre l'inscription"}
        </Button>
      </div>
    </Form>;
}